// TODO-electron-tech-debt-bedirhan: Move this component under library/v2
import { h, Fragment, ComponentChildren } from "preact";
import { useState, useLayoutEffect, useEffect, useRef } from "preact/hooks";

import classname from "../../foundation/utils/classname";
import IconElevator from "../v2/Icons/IconElevator";
import Portal from "../Portal";

import "./style.css";

interface Props {
    class: string;
    animation?: "slide" | "fade";
    hoveredItemDistanceToTop?: number;
    icon?: JSX.Element;
    leftOffset?: number;
    children: ComponentChildren;
    open: boolean;
    dragMode: boolean;
    shouldAdaptPosition?: boolean;
    onOpen: () => void;
    onClose: () => void;
    onContentRef: (el: HTMLDivElement | null) => void;
}

const ELEVATOR_OPEN_DELAY = 150;
const ELEVATOR_CONTENT_MARGIN_TOP = 78;
const ELEVATOR_CONTENT_MARGIN_BOTTOM = 24;
const SCROLL_UP_HEIGHT_INCREASE_MULTIPLIER = 0.99;
const SCROLL_DOWN_HEIGHT_INCREASE_MULTIPLIER = 3;
const MAX_HEIGHT_LIMIT = window.innerHeight - ELEVATOR_CONTENT_MARGIN_TOP - ELEVATOR_CONTENT_MARGIN_BOTTOM;
const ANIMATIONS_VALUES = {
    slide: {
        open: 300,
        close: 350
    },
    fade: {
        open: 150,
        close: 150
    }
};

function Elevator({
    class: cssClass,
    open,
    hoveredItemDistanceToTop = 0,
    onOpen,
    onClose,
    onContentRef,
    dragMode,
    children,
    animation = "fade",
    shouldAdaptPosition,
    icon,
    leftOffset = 0
}: Props) {
    const iconRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const openRef = useRef(open);
    const openTimeoutRef = useRef<number>(null);
    const [opening, setOpening] = useState(false);
    const [closing, setClosing] = useState(false);
    const animationValues = ANIMATIONS_VALUES[animation];
    let scrollTop = 0;
    let shouldMaximizeHeightOnScroll = true;

    function handleIconMouseEnter() {
        if (dragMode) {
            return;
        }

        openTimeoutRef.current = window.setTimeout(initiateOpen, ELEVATOR_OPEN_DELAY);
    }

    function handleIconMouseLeave() {
        clearTimeout(openTimeoutRef.current);
    }

    function initiateOpen() {
        clearTimeout(openTimeoutRef.current);
        setOpening(true);
        setTimeout(handleOpen, animationValues.open);
    }

    function handleOpen() {
        onOpen();
        setOpening(false);
    }

    function handleContentMouseLeave() {
        if (!openRef.current || opening || dragMode) {
            return;
        }

        setClosing(true);
        setTimeout(handleClose, animationValues.close);
    }

    function handleClose() {
        onClose();
        setClosing(false);

        shouldMaximizeHeightOnScroll = true;
    }

    function handleContentRef(el: HTMLDivElement | null) {
        contentRef.current = el;
        onContentRef(el);
    }

    function handleScroll() {
        if (!shouldMaximizeHeightOnScroll || !shouldAdaptPosition || !contentRef.current) {
            return;
        }

        const newScrollTop = contentRef.current.scrollTop;
        const contentHeight = contentRef.current.clientHeight;
        const contentTop = contentRef.current.offsetTop;
        let heightIncrease = 0;

        if (newScrollTop > scrollTop) {
            heightIncrease = (newScrollTop - scrollTop) * SCROLL_DOWN_HEIGHT_INCREASE_MULTIPLIER;

            if (contentTop !== ELEVATOR_CONTENT_MARGIN_TOP) {
                contentRef.current.scrollTop = 0;
            }
        } else if (contentTop === ELEVATOR_CONTENT_MARGIN_TOP) {
            heightIncrease = (scrollTop - newScrollTop) * SCROLL_UP_HEIGHT_INCREASE_MULTIPLIER;
        }

        const maxHeight = Math.min(MAX_HEIGHT_LIMIT, contentHeight + heightIncrease);
        const currentBottom = window.innerHeight - contentHeight - contentTop;

        contentRef.current.style.maxHeight = `${maxHeight}px`;

        if (currentBottom > ELEVATOR_CONTENT_MARGIN_BOTTOM) {
            const bottom = Math.max(ELEVATOR_CONTENT_MARGIN_BOTTOM, currentBottom - heightIncrease);

            contentRef.current.style.bottom = `${bottom}px`;
        }

        if (maxHeight === MAX_HEIGHT_LIMIT) {
            shouldMaximizeHeightOnScroll = false;
        }

        scrollTop = newScrollTop;
    }

    function handlePortalDidRender(node: HTMLDivElement) {
        if (!iconRef.current || !contentRef.current) {
            return;
        }

        const iconRect = iconRef.current.getBoundingClientRect();
        let maxHeight = MAX_HEIGHT_LIMIT;
        let contentScrollTop = 0;

        if (shouldAdaptPosition) {
            const nodeRect = node.getBoundingClientRect();
            const distanceToTop = hoveredItemDistanceToTop - iconRect.height / 2;
            const actualBottom = window.innerHeight - iconRect.top - nodeRect.height;
            const bottom = Math.max(ELEVATOR_CONTENT_MARGIN_BOTTOM, actualBottom + distanceToTop);

            maxHeight = window.innerHeight - iconRect.top - bottom;

            const remainingHeight = window.innerHeight - maxHeight - bottom - ELEVATOR_CONTENT_MARGIN_TOP;

            if (distanceToTop < remainingHeight) {
                maxHeight += distanceToTop;
            } else {
                maxHeight += remainingHeight;
                contentScrollTop = distanceToTop - remainingHeight;
            }

            node.style.bottom = `${bottom}px`;
            node.style.transform = `translateX(${iconRect.left - leftOffset}px)`;
        } else {
            const iconMidPoint = (iconRect.top + iconRect.bottom) / 2;
            const iconPosRatio = iconMidPoint / window.innerHeight;
            const contentTopValue = Math.max(
                iconMidPoint - contentRef.current.clientHeight * iconPosRatio, ELEVATOR_CONTENT_MARGIN_TOP
            );

            node.style.top = `${contentTopValue}px`;
        }

        if (maxHeight === MAX_HEIGHT_LIMIT) {
            shouldMaximizeHeightOnScroll = false;
        }

        node.style.maxHeight = `${maxHeight}px`;
        node.scrollTop = contentScrollTop;
    }

    useEffect(() => {
        if (contentRef.current) {
            ({ scrollTop } = contentRef.current);

            contentRef.current.addEventListener("scroll", handleScroll);
        }

        return () => {
            if (contentRef.current) {
                contentRef.current.removeEventListener("scroll", handleScroll);
            }
        };
    }, [contentRef.current]);

    useLayoutEffect(() => {
        openRef.current = open;
    }, [open]);

    return (
        <>
            <div
                class={classname("elevatorIconWrapper", { visible: open || opening })}
                ref={iconRef}
                onMouseEnter={handleIconMouseEnter}
                onMouseLeave={handleIconMouseLeave}>
                {icon || <IconElevator/>}
            </div>
            {(opening || open) && (
                <div class="elevatorBackdrop">
                    <Portal didRender={handlePortalDidRender}>
                        <div
                            class={classname("elevatorContent", { open, closing, shouldAdaptPosition }, cssClass)}
                            ref={handleContentRef}
                            onMouseLeave={handleContentMouseLeave}>
                            {children}
                        </div>
                    </Portal>
                </div>
            )}
        </>
    );
}

export default Elevator;
